import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../../core/apis";
import { useStores } from "../../../core/stores";
import ReminderModuleModel from "../../../modules/Reminder/ReminderModuleModel";
import type {
  Event,
  EventHeader,
  EventModule,
  EventTeamModule,
  EventToday,
} from "../../scheduleTypes";
import { useBusinessTeamSection } from "./useBusinessTeamModules";

export const useScheduleItems = (
  businessPlanUid: string,
  showPast?: boolean,
) => {
  const { t } = useTranslation();
  const { businessPlansStore, businessScheduleStore } = useStores();
  const { businessScheduleItemApi } = useApi();
  const teamModules = useBusinessTeamSection()?.modules;

  useEffect(() => {
    return businessScheduleItemApi.watchScheduleDataForBusinessPlanUid(
      businessPlanUid,
    );
  }, [businessScheduleItemApi, businessPlanUid]);

  const items = businessScheduleStore.getByBusinessPlanUid(businessPlanUid);

  const businessPlan = businessPlansStore.byId.get(businessPlanUid);

  return useMemo(() => {
    const sortedList: (Event | EventToday | EventModule | EventTeamModule)[] = [
      ...(items.map((item) => ({
        ...item.data,
        date: item.calculatedDate,
        type: "item",
      })) as Event[]),
      ...((businessPlan?.scheduleBusinessModules ?? []).map((module) => ({
        uid: module.uid,
        type: "module",
        date: module.calculatedDate.toDate().toISOString().split("T")[0],
        module,
        title: `${module.data.emoji} ${module.data.title}`,
      })) as EventModule[]),
      ...((teamModules ?? [])
        .filter(
          (module): module is ReminderModuleModel =>
            module instanceof ReminderModuleModel,
        )
        .map((module) => ({
          uid: module.uid,
          type: "team-module",
          date: module.data.dateDue.toDate().toISOString().split("T")[0],
          module,
          title: `${module.data.emoji} ${module.data.title}`,
        })) as EventTeamModule[]),
      {
        type: "today",
        date: new Date().toISOString().split("T")[0],
        title: t("business:today"),
      } as EventToday,
    ].toSorted((a, b) => (a.date > b.date ? 1 : -1));

    const groupedList: (
      | Event
      | EventToday
      | EventHeader
      | EventModule
      | EventTeamModule
    )[] = [];

    const list: (Event | EventModule | EventTeamModule)[] = [];

    let previousMonthAndYear: string | undefined;

    let hasPastEvents = false;

    for (const item of sortedList) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of today

      const eventDate = new Date(item.date);
      eventDate.setHours(0, 0, 0, 0); // Set to start of event day

      const isPast = eventDate < today;

      if (
        isPast &&
        ((item.type !== "module" && item.type !== "team-module") ||
          item.module.isCompletedByUser)
      ) {
        hasPastEvents = true;

        if (!showPast) {
          continue;
        }
      }
      const currentMonthAndYear = dayjs(item.date).format("YYYY-MM");

      if (currentMonthAndYear !== previousMonthAndYear) {
        groupedList.push({
          title: dayjs(new Date(`${currentMonthAndYear}-01`)).format(
            "MMM YYYY",
          ),
          type: "header",
        });
        previousMonthAndYear = currentMonthAndYear;
      }

      groupedList.push(item);

      if (item.type !== "today") {
        list.push(item);
      }
    }

    return { groupedList, hasPastEvents, list };
  }, [t, businessPlan?.scheduleBusinessModules, showPast, items, teamModules]);
};
